import { ChevronDown, ChevronUp } from 'lucide-react';
import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { ProgressRing } from '@/components/setup/ProgressRing';
import { SetupChecklist } from '@/components/setup/SetupChecklist';
import { useSetupProgress } from '@/hooks/useSetupProgress';

/**
 * Persistent sidebar setup footer (IX-3929).
 *
 * A ring + "Finish setting up Rose" summary that follows the user across the app until
 * onboarding is done, and the panel it opens: every step (done / current /
 * to-do), each linking to the surface that clears it. Hidden when the sidebar is
 * collapsed to icons, and once the site is live.
 *
 * The panel is a plain fixed dock rather than a popover (IX-4114). A popover
 * dismisses on outside click and unmounts with its trigger, so clicking a step
 * closed it and a fresh one appeared on the next page. `AppLayout` also swaps
 * `Sidebar` for `StudioSidebar` on Config Studio routes, which remounts this
 * component — hence localStorage rather than component state.
 */
const PANEL_OPEN_KEY = 'rose_setup_panel_open';

function readPanelOpen(): boolean {
    try {
        // Default open: the panel is the guide through Day-1, so it should be
        // there until the user deliberately minimises it.
        return window.localStorage.getItem(PANEL_OPEN_KEY) !== 'false';
    } catch {
        return true;
    }
}

export function SidebarSetupFooter() {
    const progress = useSetupProgress();
    const [open, setOpen] = useState(readPanelOpen);

    const toggle = useCallback((next: boolean) => {
        setOpen(next);
        try {
            window.localStorage.setItem(PANEL_OPEN_KEY, String(next));
        } catch {
            // Storage unavailable — the panel just won't remember across routes.
        }
    }, []);

    // Setup done (or still loading): no steps to show.
    if (progress.loading || progress.isLive) return null;

    return (
        <div className="mt-auto px-2 pb-1 group-data-[collapsible=icon]:hidden">
            <button
                type="button"
                onClick={() => toggle(!open)}
                aria-expanded={open}
                className="flex w-full items-center gap-[11px] rounded-xl bg-accent-faint p-3 text-left shadow-[inset_0_0_0_1.5px_var(--brand-200)] transition-[filter] hover:brightness-[0.98]"
            >
                <ProgressRing pct={progress.readyPct} size="sm" />
                <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-bold text-[color:var(--brand-700)]">
                        {progress.readyToPublish ? 'Ready to publish' : 'Finish setting up Rose'}
                    </span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                        {progress.readyCount} of {progress.readyTotal} · {progress.nextLabel}
                    </span>
                </span>
                <ChevronUp className="size-4 flex-none text-foreground-subtle" />
            </button>

            {open &&
                createPortal(
                    // `left-2` / `bottom-2` is the sidebar's own gutter (its
                    // footer is `px-2`, this subtree `pb-1`), so the panel lands
                    // where the chip it replaces sat instead of leaving a strip
                    // of sidebar showing down its left edge.
                    <div className="fixed bottom-2 left-2 z-40 flex max-h-[calc(100vh-1rem)] w-72 flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-[0_12px_36px_rgba(15,23,36,0.12)]">
                        <div className="flex items-center gap-3.5 border-b border-border-subtle px-[18px] py-4">
                            <ProgressRing pct={progress.readyPct} size="md" />
                            <span className="min-w-0 flex-1">
                                <span className="block text-[15px] font-semibold text-foreground">
                                    {progress.readyToPublish
                                        ? 'Ready to publish'
                                        : 'Finish setting up Rose'}
                                </span>
                                <span className="mt-0.5 block text-xs text-muted-foreground">
                                    {/* Once everything but publish is done, the count reads as
                                        unfinished work next to "Ready to publish" — it can never
                                        reach n/n before the step that closes the panel. Name the
                                        remaining action instead. */}
                                    {progress.readyToPublish
                                        ? 'Add the snippet to go live'
                                        : `${progress.readyCount} of ${progress.readyTotal} steps done`}
                                </span>
                            </span>
                            <button
                                type="button"
                                onClick={() => toggle(false)}
                                aria-label="Minimise setup panel"
                                className="flex size-7 flex-none items-center justify-center rounded-lg text-foreground-subtle transition-colors hover:bg-surface-sunken"
                            >
                                <ChevronDown className="size-4" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto px-3 pt-2.5 pb-3.5">
                            <SetupChecklist progress={progress} />
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
}
